// ============================================================
// ARCHIVO: src/components/MisCalificacionesModal.js
// ============================================================
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Dialog, DialogTitle, DialogContent, IconButton,
  Box, Typography, Stack, CircularProgress, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Tabs, Tab, TextField, MenuItem, Chip, Popover,
} from "@mui/material";
import { FaTimes, FaGraduationCap } from "react-icons/fa";
import { auth } from "../firebase";

const API_BASE = "https://vol-backend.onrender.com";
const colorRojo = "#ff3333";

async function authedFetch(url) {
  const user = auth.currentUser;
  if (!user) throw new Error("No autenticado");
  const token = await user.getIdToken();
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(txt || `HTTP ${res.status}`);
  }
  return res.json();
}

function fmt(n) {
  if (n === null || n === undefined || n === "") return "—";
  return Number(n).toFixed(2);
}

function EstadoChip({ status }) {
  const map = {
    completed:   { color: "#1b5e20", bg: "#e8f5e9", label: "Completado" },
    completado:  { color: "#1b5e20", bg: "#e8f5e9", label: "Completado" },
    graded:      { color: "#1b5e20", bg: "#e8f5e9", label: "Calificada" },
    approved:    { color: "#1b5e20", bg: "#e8f5e9", label: "Calificada" },
    in_progress: { color: "#e65100", bg: "#fff3e0", label: "En progreso" },
    submitted:   { color: "#e65100", bg: "#fff3e0", label: "Pendiente" },
    not_started: { color: "#b00020", bg: "#ffebee", label: "Sin Entregar" },
    aprobado:    { color: "#1b5e20", bg: "#e8f5e9", label: "Aprobado" },
    en_progreso: { color: "#e65100", bg: "#fff3e0", label: "En progreso" },
    reprobado:   { color: "#b00020", bg: "#ffebee", label: "Reprobado" },
    bloqueado:   { color: "#9e9e9e", bg: "#f5f5f5", label: "Bloqueado" },
  };
  const s = map[status] || { color: "#9e9e9e", bg: "#f5f5f5", label: status || "Sin Entregar" };
  return (
    <Chip size="small" label={s.label}
      sx={{ backgroundColor: s.bg, color: s.color, fontWeight: 800, fontSize: 11 }} />
  );
}

function celdaActividadSubida(r) {
  if (r.calificacion !== null && r.calificacion !== undefined) {
    return (
      <Stack spacing={0.3} alignItems="center">
        <Chip size="small" label="Calificada"
          sx={{ backgroundColor: "#e8f5e9", color: "#1b5e20", fontWeight: 800, fontSize: 10 }} />
      </Stack>
    );
  }
  if (r.file_url) {
    return <Chip size="small" label="Pendiente" sx={{ backgroundColor: "#fff3e0", color: "#e65100", fontWeight: 800, fontSize: 10 }} />;
  }
  return <Chip size="small" label="Sin Entregar" sx={{ backgroundColor: "#ffebee", color: "#b00020", fontWeight: 800, fontSize: 10 }} />;
}

function ComentarioBubble({ comentario, fecha }) {
  const [anchor, setAnchor] = useState(null);
  if (!comentario) return null;
  return (
    <>
      <IconButton size="small"
        onClick={e => setAnchor(e.currentTarget)}
        sx={{
          ml: 0.5, p: 0.3,
          backgroundColor: "#e3f2fd",
          color: "#0d47a1",
          "&:hover": { backgroundColor: "#bbdefb" },
        }}>
        <span style={{ fontSize: 13 }}>💬</span>
      </IconButton>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{ zIndex: 20000 }}
        PaperProps={{ sx: { borderRadius: 2, p: 1.5, maxWidth: 280, boxShadow: 3 } }}
      >
        <Typography sx={{ fontSize: 12, color: "#0d47a1", fontWeight: 700, mb: 0.5 }}>
          Comentario del facilitador
        </Typography>
        <Typography sx={{ fontSize: 12, color: "#333", lineHeight: 1.5 }}>
          {comentario}
        </Typography>
        {fecha && (
          <Typography sx={{ fontSize: 10, color: "#888", mt: 0.8 }}>
            {new Date(fecha).toLocaleDateString("es-MX", {
              day: "2-digit", month: "short", year: "numeric"
            })}
          </Typography>
        )}
      </Popover>
    </>
  );
}

// ─── Pestaña 1: Detallada ──────────────────────────────────────
function TabDetallada({ actividades, programaFiltro, bloqueFiltro }) {
  const filtradas = useMemo(() => {
    return actividades.filter(a => {
      if (programaFiltro && a.programa_code !== programaFiltro) return false;
      if (bloqueFiltro && String(a.block_id) !== String(bloqueFiltro)) return false;
      return true;
    });
  }, [actividades, programaFiltro, bloqueFiltro]);

  const estructura = useMemo(() => {
    const acc = {};
    for (const a of filtradas) {
      const pk = a.programa_code;
      if (!acc[pk]) acc[pk] = { nombre: a.programa_nombre, bloques: {} };
      const bk = a.block_id;
      if (!acc[pk].bloques[bk]) acc[pk].bloques[bk] = { nombre: a.bloque_nombre, orden: a.bloque_orden, modulos: {} };
      const mk = a.module_id;
      if (!acc[pk].bloques[bk].modulos[mk]) acc[pk].bloques[bk].modulos[mk] = { nombre: a.modulo_nombre, orden: a.modulo_orden, actividades: [] };
      acc[pk].bloques[bk].modulos[mk].actividades.push(a);
    }
    return acc;
  }, [filtradas]);

  const complementarias = (acts) => acts.filter(a => a.actividad_tipo !== 'upload' && a.actividad_tipo !== 'docs' && a.actividad_tipo !== 'documento' && a.actividad_tipo !== 'quiz');
  const quizFinal       = (acts) => acts.filter(a => a.actividad_tipo === 'quiz' || a.is_final_exam);
  const extraordinario = (acts) => acts.filter(a => a.actividad_tipo === 'extraordinary');
  const subidas         = (acts) => acts.filter(a => a.actividad_tipo === 'upload' || a.actividad_tipo === 'docs' || a.actividad_tipo === 'documento');

  if (Object.keys(estructura).length === 0) {
    return <Typography sx={{ color: "#777", textAlign: "center", py: 5 }}>Sin actividades para mostrar.</Typography>;
  }

  return (
    <Box sx={{ maxHeight: 560, overflowY: "auto" }}>
      {Object.entries(estructura).map(([progCode, progGrupo]) => (
        <Box key={progCode} sx={{ mb: 3 }}>
          <Box sx={{ backgroundColor: "#ede7f6", fontWeight: 900, fontSize: 13, color: "#4a148c",
                     py: 1, px: 2, borderRadius: 1, mb: 1.5 }}>
            📚 {progGrupo.nombre} ({progCode})
          </Box>

          {Object.entries(progGrupo.bloques)
            .sort((a, b) => (a[1].orden || 0) - (b[1].orden || 0))
            .map(([blqId, blqGrupo]) => (
            <Box key={blqId} sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "#9c27b0", mb: 1, px: 1 }}>
                Bloque {blqGrupo.orden} · {blqGrupo.nombre}
              </Typography>

              {Object.entries(blqGrupo.modulos)
                .sort((a, b) => (a[1].orden || 0) - (b[1].orden || 0))
                .map(([modId, modGrupo]) => {
                  const acts = modGrupo.actividades;
                  return (
                    <Box key={modId} sx={{ mb: 2, ml: 1 }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#111", mb: 0.6 }}>
                        Módulo {modGrupo.orden} · {modGrupo.nombre}
                      </Typography>

                      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#777", mb: 0.5 }}>
                            Actividades Complementarias
                          </Typography>
                          <TableContainer sx={{ border: "1px solid #eee", borderRadius: 1.5 }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontSize: 10.5, fontWeight: 900, backgroundColor: "#fafafa" }}>Actividad</TableCell>
                                  <TableCell sx={{ fontSize: 10.5, fontWeight: 900, backgroundColor: "#fafafa" }}>Tipo</TableCell>
                                  <TableCell sx={{ fontSize: 10.5, fontWeight: 900, backgroundColor: "#fafafa" }}>Estado</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {complementarias(acts).map(a => (
                                  <TableRow key={a.activity_id}>
                                    <TableCell sx={{ fontSize: 11 }}>{a.actividad_nombre}</TableCell>
                                    <TableCell sx={{ fontSize: 11, textTransform: "capitalize" }}>{a.actividad_tipo}</TableCell>
                                    <TableCell><EstadoChip status={a.actividad_status || "not_started"} /></TableCell>
                                  </TableRow>
                                ))}
                                {quizFinal(acts).map(a => (
                                  <TableRow key={a.activity_id} sx={{ backgroundColor: "#fff8e1" }}>
                                    <TableCell sx={{ fontSize: 11, fontWeight: 800 }}>Evaluación Teórica del Módulo</TableCell>
                                    <TableCell sx={{ fontSize: 11 }}>Quiz Final</TableCell>
                                    <TableCell sx={{ fontSize: 12, fontWeight: 900, color: a.calificacion >= 8 ? "#1b5e20" : "#b00020" }}>
                                      {fmt(a.calificacion)}
                                    </TableCell>
                                  </TableRow>
                                ))}
                                {(() => {
                                  const extras = extraordinario(acts);
                                  const tieneQuiz = quizFinal(acts).length > 0;
                                  if (!tieneQuiz) return null;
                                  if (extras.length === 0) {
                                    return (
                                      <TableRow sx={{ backgroundColor: "#f3e5f5" }}>
                                        <TableCell sx={{ fontSize: 11, fontWeight: 800, color: "#6a1b9a" }}>Evaluación Extraordinaria</TableCell>
                                        <TableCell sx={{ fontSize: 11 }}>Extraordinario</TableCell>
                                        <TableCell sx={{ fontSize: 11, color: "#9e9e9e", fontWeight: 700 }}>N/A</TableCell>
                                      </TableRow>
                                    );
                                  }
                                  return extras.map(a => (
                                    <TableRow key={a.activity_id} sx={{ backgroundColor: "#f3e5f5" }}>
                                      <TableCell sx={{ fontSize: 11, fontWeight: 800, color: "#6a1b9a" }}>Evaluación Extraordinaria</TableCell>
                                      <TableCell sx={{ fontSize: 11 }}>Extraordinario</TableCell>
                                      <TableCell sx={{ fontSize: 12, fontWeight: 900, color: a.calificacion >= 8 ? "#1b5e20" : "#b00020" }}>
                                        {fmt(a.calificacion)}
                                      </TableCell>
                                    </TableRow>
                                  ));
                                })()}
                                {complementarias(acts).length === 0 && quizFinal(acts).length === 0 && (
                                  <TableRow><TableCell colSpan={3} sx={{ fontSize: 11, color: "#aaa", textAlign: "center" }}>—</TableCell></TableRow>
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>

                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#777", mb: 0.5 }}>
                            Actividades Subidas
                          </Typography>
                          <TableContainer sx={{ border: "1px solid #eee", borderRadius: 1.5 }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontSize: 10.5, fontWeight: 900, backgroundColor: "#fafafa" }}>Actividad</TableCell>
                                  <TableCell sx={{ fontSize: 10.5, fontWeight: 900, backgroundColor: "#fafafa" }}>Estado</TableCell>
                                  <TableCell sx={{ fontSize: 10.5, fontWeight: 900, backgroundColor: "#fafafa" }}>Calificación</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                               {subidas(acts).map(a => (
                                <TableRow key={a.activity_id}>
                                  <TableCell sx={{ fontSize: 11 }}>{a.actividad_nombre}</TableCell>
                                  <TableCell>{celdaActividadSubida(a)}</TableCell>
                                  <TableCell sx={{ fontSize: 12, fontWeight: 900 }}>
                                    <Stack direction="row" alignItems="center" spacing={0.3}>
                                      {fmt(a.calificacion)}
                                      <ComentarioBubble
                                        comentario={a.comentario_facilitador}
                                        fecha={a.fecha_comentario}
                                      />
                                    </Stack>
                                  </TableCell>
                                </TableRow>
                              ))}
                                {subidas(acts).length === 0 && (
                                  <TableRow><TableCell colSpan={3} sx={{ fontSize: 11, color: "#aaa", textAlign: "center" }}>—</TableCell></TableRow>
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      </Stack>
                    </Box>
                  );
                })}
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
}

// ─── Pestaña 2: General por Programa ───────────────────────────
function TabGeneral({ general, programaFiltro, bloqueFiltro }) {
  const filtrado = useMemo(() => {
    let progs = general;
    if (programaFiltro) progs = progs.filter(p => p.programa_code === programaFiltro);
    return progs.map(p => ({
      ...p,
      bloques: bloqueFiltro ? p.bloques.filter(b => String(b.block_id) === String(bloqueFiltro)) : p.bloques,
    }));
  }, [general, programaFiltro, bloqueFiltro]);

  if (filtrado.length === 0) {
    return <Typography sx={{ color: "#777", textAlign: "center", py: 5 }}>Sin datos para mostrar.</Typography>;
  }

  return (
    <Box sx={{ maxHeight: 560, overflowY: "auto" }}>
      {filtrado.map(prog => (
        <Box key={prog.programa_code} sx={{ mb: 3, border: "1px solid #eee", borderRadius: 2, overflow: "hidden" }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center"
                 sx={{ backgroundColor: "#fafafa", p: 1.5 }}>
            <Typography sx={{ fontWeight: 900, fontSize: 13, color: colorRojo }}>
              {prog.programa_nombre} ({prog.programa_code})
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography sx={{ fontSize: 11, color: "#777" }}>Calificación del Programa</Typography>
              <Typography sx={{ fontWeight: 900, fontSize: 16, color: "#1b5e20" }}>
                {fmt(prog.calificacion_programa)}
              </Typography>
            </Stack>
          </Stack>

          {prog.bloques.map(b => (
            <Box key={b.block_id} sx={{ borderTop: "1px solid #eee" }}>
              <Stack direction="row" sx={{ flexWrap: "wrap" }}>
                <Box sx={{ flex: 2, minWidth: 280, p: 1.2, borderRight: "1px solid #eee" }}>
                  <Typography sx={{ fontWeight: 900, fontSize: 12, color: "#fff", backgroundColor: colorRojo,
                                     px: 1, py: 0.5, borderRadius: 1, mb: 1 }}>
                    Calificación Bloque {b.bloque_orden} · {b.bloque_nombre}
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontSize: 10.5, fontWeight: 900 }}>Módulo</TableCell>
                          <TableCell sx={{ fontSize: 10.5, fontWeight: 900 }}>Uploads</TableCell>
                          <TableCell sx={{ fontSize: 10.5, fontWeight: 900 }}>Quiz</TableCell>
                          <TableCell sx={{ fontSize: 10.5, fontWeight: 900 }}>Nota Módulo</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {b.modulos.map(m => (
                          <TableRow key={m.module_id}>
                            <TableCell sx={{ fontSize: 11 }}>{m.modulo_nombre}</TableCell>
                            <TableCell sx={{ fontSize: 11 }}>{fmt(m.uploads_avg)}</TableCell>
                            <TableCell sx={{ fontSize: 11 }}>{fmt(m.quiz_score)}</TableCell>
                            <TableCell sx={{ fontSize: 12, fontWeight: 900, color: "#1b5e20" }}>{fmt(m.theoretical_grade)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Box sx={{ flex: 1.4, minWidth: 220, p: 1.2, borderRight: "1px solid #eee" }}>
                  <Typography sx={{ fontWeight: 900, fontSize: 12, color: "#fff", backgroundColor: colorRojo,
                                     px: 1, py: 0.5, borderRadius: 1, mb: 1, textAlign: "center" }}>
                    {fmt(b.teoria_final)}
                  </Typography>
                  <Typography sx={{ fontSize: 10.5, color: "#777", textAlign: "center", mb: 0.5 }}>Teoría Final del Bloque</Typography>
                  <Stack direction="row" justifyContent="space-around" sx={{ mt: 1 }}>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography sx={{ fontSize: 10, color: "#777" }}>Promedio teórico</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 900 }}>{fmt(b.promedio_teorico_bloque)}</Typography>
                    </Box>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography sx={{ fontSize: 10, color: "#777" }}>Extraordinario</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 900 }}>{fmt(b.theoretical_extra_score)}</Typography>
                    </Box>
                  </Stack>
                </Box>

                <Box sx={{ flex: 1, minWidth: 180, p: 1.2 }}>
                  <Typography sx={{ fontWeight: 900, fontSize: 12, textAlign: "center", py: 0.5, borderRadius: 1, mb: 1,
                                     backgroundColor: b.has_practical_exam ? "#e8f5e9" : "#fff3e0",
                                     color: b.has_practical_exam ? "#1b5e20" : "#e65100" }}>
                    {b.has_practical_exam ? "Activada" : "Desactivada"}
                  </Typography>
                  <Typography sx={{ fontSize: 10.5, color: "#777", textAlign: "center" }}>
                    {Number(b.weight_practica) * 100}% Evaluación Práctica
                  </Typography>
                  <Box sx={{ textAlign: "center", mt: 1.5 }}>
                    {b.has_practical_exam ? (
                      <Typography sx={{ fontSize: 28, fontWeight: 900, color: "#1b5e20" }}>
                        {fmt(b.practica_final)}
                      </Typography>
                    ) : (
                      <Typography sx={{ fontSize: 11, color: "#aaa" }}>No Aplica para este bloque</Typography>
                    )}
                  </Box>
                </Box>
              </Stack>

              <Stack direction="row" justifyContent="flex-end" sx={{ p: 1, backgroundColor: "#fafafa" }}>
                <Typography sx={{ fontSize: 11, color: "#777", mr: 1 }}>Nota final del bloque:</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 900, color: Number(b.nota_final_bloque) >= 8 ? "#1b5e20" : "#b00020" }}>
                  {fmt(b.nota_final_bloque)}
                </Typography>
              </Stack>
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
}

export default function MisCalificacionesModal({ open, onClose }) {
  const [tab, setTab] = useState(0);
  const [data, setData] = useState({ detallada: [], general: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [programaFiltro, setProgramaFiltro] = useState("");
  const [bloqueFiltro, setBloqueFiltro] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const res = await authedFetch(`${API_BASE}/progreso/me/calificaciones`);
      setData({ detallada: res.detallada || [], general: res.general || [] });
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const programas = useMemo(() => {
    const set = new Map();
    data.detallada.forEach(a => set.set(a.programa_code, a.programa_nombre));
    return Array.from(set.entries());
  }, [data.detallada]);

  const bloquesDelPrograma = useMemo(() => {
    if (!programaFiltro) return [];
    const set = new Map();
    data.detallada.filter(a => a.programa_code === programaFiltro)
      .forEach(a => set.set(a.block_id, a.bloque_nombre));
    return Array.from(set.entries());
  }, [data.detallada, programaFiltro]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth sx={{ zIndex: 10000,"& .MuiDialog-container": {paddingLeft: { md: "260px" }}}}
     PaperProps={{sx: {borderRadius: 3,maxHeight: "92vh",width: "calc(100vw - 300px)",maxWidth: "none",}}}>
      <DialogTitle sx={{ borderBottom: "1px solid #eee", pb: 1.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Stack direction="row" spacing={1.2} alignItems="center">
            <Box sx={{ backgroundColor: colorRojo, borderRadius: "50%", p: 1, display: "flex" }}>
              <FaGraduationCap color="#fff" size={20} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 900, fontSize: "1.2rem", color: "#111" }}>
                Mis Calificaciones
              </Typography>
              <Typography sx={{ fontSize: "0.78rem", color: "#777", maxWidth: 480 }}>
                Verifica tus calificaciones en los programas que cuentan con actividad. Si subiste tu actividad
                y aparece en pendiente, favor de comunicarse con tu coordinador estatal para que la evalúe a la brevedad.
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} size="small"><FaTimes size={16} /></IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 2.5, pt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2,
          "& .MuiTab-root": { fontWeight: 900, fontSize: "0.82rem", textTransform: "none" },
          "& .Mui-selected": { color: colorRojo },
          "& .MuiTabs-indicator": { backgroundColor: colorRojo } }}>
          <Tab label="Detallada" />
          <Tab label="General por Programa" />
        </Tabs>

        <Stack direction="row" spacing={1.5} flexWrap="wrap" sx={{ mb: 2 }}>
          <TextField select size="small" label="Programa" value={programaFiltro}
            onChange={e => { setProgramaFiltro(e.target.value); setBloqueFiltro(""); }}
            sx={{ width: 280 }}
            SelectProps={{ MenuProps: { sx: { zIndex: 20000 } } }}>
            <MenuItem value="">Todos los programas</MenuItem>
            {programas.map(([code, name]) => (
              <MenuItem key={code} value={code}>{name} ({code})</MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label="Bloque" value={bloqueFiltro}
            onChange={e => setBloqueFiltro(e.target.value)}
            disabled={!programaFiltro}
            sx={{ width: 240 }}
            SelectProps={{ MenuProps: { sx: { zIndex: 20000 } } }}>
            <MenuItem value="">Todos los bloques</MenuItem>
            {bloquesDelPrograma.map(([id, name]) => (
              <MenuItem key={id} value={id}>{name}</MenuItem>
            ))}
          </TextField>
        </Stack>

        {loading ? (
          <Stack alignItems="center" sx={{ py: 5 }}><CircularProgress size={28} /></Stack>
        ) : tab === 0 ? (
          <TabDetallada actividades={data.detallada} programaFiltro={programaFiltro} bloqueFiltro={bloqueFiltro} />
        ) : (
          <TabGeneral general={data.general} programaFiltro={programaFiltro} bloqueFiltro={bloqueFiltro} />
        )}

        <Box sx={{ mt: 2.5, p: 1.5, backgroundColor: "#fff8f8", borderRadius: 2, border: "1px solid #ffe0e0" }}>
          <Typography sx={{ fontSize: 11.5, color: "#777" }}>
            ℹ️ Si subiste tu actividad y aparece en pendiente, favor de comunicarse con su coordinador
            estatal para que él la evalúe a la brevedad.
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
}